import React, { useState } from 'react';
import styled from 'styled-components';
import FileUploader from '../components/FileUploader';
import FolderNavigator from '../components/FolderNavigator';
import ImageAnnotator from '../components/ImageAnnotator';
import { FolderStructure, ImageData, Annotation } from '../types';
import { getExifData } from '../utils/zipUtils';

const PageContainer = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  overflow: hidden;
`;

const LeftPanel = styled.div`
  width: 300px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background: white;
`;

const RightPanel = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const InspectionViewer: React.FC = () => {
    const [folderStructure, setFolderStructure] = useState<FolderStructure>({ turbineName: '', blades: {} });
    const [images, setImages] = useState<ImageData[]>([]);
    const [selectedBlade, setSelectedBlade] = useState<string | null>(null);
    const [selectedSurface, setSelectedSurface] = useState<string | null>(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [annotations, setAnnotations] = useState<Annotation[]>([]);
    const [showUploader, setShowUploader] = useState(true);
    const [isenrichingExif, setIsEnrichingExif] = useState(false);
    const [projectId, setProjectId] = useState<string | null>(null); // Track current project ID

    // EXIF enrichment geçici olarak devre dışı (timeout sorunları nedeniyle)
    // Kullanıcı manuel olarak EXIF bilgisi ekleyebilir veya gelecekte daha hızlı kütüphane kullanılabilir
    React.useEffect(() => {
        // Disabled - EXIF timeout causing infinite loop
        // Will re-enable with faster EXIF library (exifreader or piexifjs)
        console.log('[EXIF] Enrichment disabled - use fresh zip upload for EXIF data');
    }, [images, isenrichingExif]);

    const handleZipProcessed = (structure: FolderStructure, imageData: ImageData[], projId: string) => {
        console.log('Zip processed:', { structure, imageCount: imageData.length, projectId: projId });
        setFolderStructure(structure);
        setImages(imageData);
        setProjectId(projId); // Store project ID
        setShowUploader(false);
        setCurrentImageIndex(0);

        // Auto-select first blade and surface if available
        const bladeNames = Object.keys(structure.blades);
        if (bladeNames.length > 0) {
            const firstBlade = bladeNames[0];
            setSelectedBlade(firstBlade);

            const surfaceNames = Object.keys(structure.blades[firstBlade].surfaces);
            if (surfaceNames.length > 0) {
                const firstSurface = surfaceNames[0];
                setSelectedSurface(firstSurface);
            }
        }
    };

    const handleSelectFolder = (bladeName: string, surfaceType: string) => {
        setSelectedBlade(bladeName);
        setSelectedSurface(surfaceType);
        setCurrentImageIndex(0);
    };

    const handleSelectImage = (index: number) => {
        setCurrentImageIndex(index);
    };

    const handleAddAnnotation = (annotation: Annotation) => {
        setAnnotations([...annotations, annotation]);
    };

    const handleDeleteAnnotation = (annotationId: string) => {
        setAnnotations(annotations.filter(a => a.id !== annotationId));
    };

    const handleUpdateAnnotation = (updatedAnnotation: Annotation) => {
        setAnnotations(annotations.map(a =>
            a.id === updatedAnnotation.id ? updatedAnnotation : a
        ));
    };

    const handleBackToUploader = () => {
        setShowUploader(true);
        setFolderStructure({ turbineName: '', blades: {} });
        setImages([]);
        setSelectedBlade(null);
        setSelectedSurface(null);
        setCurrentImageIndex(0);
        setAnnotations([]);
    };

    if (showUploader || images.length === 0) {
        return (
            <div style={{ padding: '30px' }}>
                <FileUploader onZipProcessed={handleZipProcessed} />
            </div>
        );
    }

    return (
        <PageContainer>
            <LeftPanel>
                <FolderNavigator
                    folderStructure={folderStructure}
                    onSelectFolder={handleSelectFolder}
                    selectedBlade={selectedBlade}
                    selectedSurface={selectedSurface}
                    images={images}
                    currentImageIndex={currentImageIndex}
                    onSelectImage={handleSelectImage}
                />
            </LeftPanel>
            <RightPanel>
                <ImageAnnotator
                    images={images}
                    annotations={annotations}
                    onAddAnnotation={handleAddAnnotation}
                    onDeleteAnnotation={handleDeleteAnnotation}
                    onUpdateAnnotation={handleUpdateAnnotation}
                    selectedBlade={selectedBlade}
                    selectedSurface={selectedSurface}
                    currentImageIndex={currentImageIndex}
                    setCurrentImageIndex={setCurrentImageIndex}
                    projectId={projectId || undefined} // Pass project ID to annotator
                />
            </RightPanel>
        </PageContainer>
    );
};

export default InspectionViewer;
